// "Where do you want to look?": the pages a person or a store can be looked at on, each
// as a link that opens the page already narrowed to them.
//
// Only pages that really open narrowed are in the tables below (hooks/useLinkedSearch
// and utils/drilldown name the pages that read a link, and how). A page that would open
// on its whole list is left out rather than offered. Of these, a person is offered the
// ones they hold, by the same menu rules page search lists pages with, and each is
// named by its own menu row.
//
// Holding a page's menu row is not a promise of access. Some pages check a second
// permission and show their own "access needed" panel; that is left to the page. The
// one exception is a page that refuses outright on a fact the browser already holds:
// Red Flags lets in only an admin or a super admin, so nobody else is offered its tile.
import { DRILL_ROUTES, buildDrillUrl, monthRange, todayIso, type DrillParams } from '../utils/drilldown';
import { dashboardPath } from './dashboards';
import type { MenuIcon } from './menuIcon';
import type { LookKind } from './searchDestinations';
import { entriesForPaths, type SearchEntry } from './searchIndex';

interface PlaceSpec {
  path: string;
  /** Two to four words for the period the link opens the page on. */
  period: string;
  /**
   * The name the code travels under: the page's own person or store filter, or `q` for
   * a page that only has a search box and reads no store from a link (the box matches
   * a store code as typed).
   */
  by: 'employeeCode' | 'storeCode' | 'q';
  /** The page opens on yesterday alone, so the link asks for this month to date. */
  thisMonth?: true;
  /** One more value the page reads from its address, written as it must appear there. */
  also?: string;
  /** The page says "Access denied" to anyone who is not an admin or a super admin, whatever their menu holds. */
  adminOnly?: true;
}

// In the order a programme team would want them.
const FOR_PERSON: readonly PlaceSpec[] = [
  { path: '/master-management/employees', period: 'Their record', by: 'employeeCode' },
  { path: DRILL_ROUTES.dailyAttendance, period: 'This month', by: 'employeeCode', thisMonth: true },
  // Red Flags opens on the 1st of this month to today by itself. Its menu row can be
  // held by someone the page (pages/ManagerFocusDebugPage) then refuses.
  { path: DRILL_ROUTES.redFlags, period: 'This month', by: 'employeeCode', adminOnly: true },
  { path: DRILL_ROUTES.appeals, period: 'All dates', by: 'employeeCode' },
  // dates=all: the page's own window is this month, and it never reads further back
  // than 60 days.
  { path: '/approvals', period: 'Last 60 days', by: 'employeeCode', also: 'dates=all' },
  { path: '/reports/employee-score', period: 'Latest month', by: 'employeeCode' },
  { path: DRILL_ROUTES.mood, period: 'This month', by: 'employeeCode', thisMonth: true },
  // A link puts the quarter picker on "All quarters".
  { path: '/reports/pip', period: 'All quarters', by: 'employeeCode' },
  { path: '/governance/start-exit', period: 'Last 12 months', by: 'employeeCode' },
  { path: dashboardPath('Onboarding'), period: 'Active plans', by: 'employeeCode' },
  // The visits this person made, when they are a manager who visits stores.
  { path: '/reports/store-visits', period: 'This quarter', by: 'employeeCode' },
];

const FOR_STORE: readonly PlaceSpec[] = [
  { path: '/master-management/stores', period: 'Its record', by: 'storeCode' },
  { path: '/reports/employee-score', period: 'Latest month', by: 'storeCode' },
  { path: '/reports/store-visits', period: 'This quarter', by: 'storeCode' },
  // tab=answers: the page opens on its compliance tab; the answers, which the link
  // narrows, are its other tab.
  { path: '/reports/store-visit-tasks', period: 'Last 30 days', by: 'storeCode', also: 'tab=answers' },
  { path: DRILL_ROUTES.dailyAttendance, period: 'This month', by: 'q', thisMonth: true },
  { path: DRILL_ROUTES.mood, period: 'This month', by: 'q', thisMonth: true },
];

/** Who or what is being looked up. A person's id is their employee code; a store's code is its own. */
export interface LookSubject {
  kind: LookKind;
  id: string;
  code?: string;
}

export interface Place {
  path: string;
  /** The page's name and icon, from its menu row. */
  name: string;
  icon: MenuIcon;
  /** What the page is for: the tile's hover text. */
  hint: string;
  period: string;
  /** The page, narrowed to the person or the store. */
  to: string;
}

/** What is known of the person looking, beyond the pages they hold. */
export interface Viewer {
  /** An admin or a super admin (the signed-in user's adminRole): what the Red Flags page lets people in on. */
  isAdmin: boolean;
}

/**
 * The places to look for this person or store, among the pages in `index` (the
 * viewer's own, from buildSearchIndex). Empty when they hold none of them, and for a
 * store that has no store code: no page could be narrowed to it.
 */
export function placesFor(subject: LookSubject, index: SearchEntry[], viewer: Viewer): Place[] {
  const code = (subject.kind === 'person' ? subject.code || subject.id : subject.code ?? '').trim();
  if (!code) return [];
  const places: Place[] = [];
  for (const spec of subject.kind === 'person' ? FOR_PERSON : FOR_STORE) {
    if (spec.adminOnly && !viewer.isAdmin) continue;
    const [held] = entriesForPaths(index, [spec.path]);
    if (!held) continue;
    const params: DrillParams = { [spec.by]: code, ...(spec.thisMonth ? monthRange(todayIso().slice(0, 7)) : {}) };
    const narrowed = buildDrillUrl(spec.path, params);
    places.push({
      path: spec.path,
      name: held.name,
      icon: held.icon,
      hint: held.line,
      period: spec.period,
      to: spec.also ? `${narrowed}&${spec.also}` : narrowed,
    });
  }
  return places;
}
